import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import SiteHeader from '../components/SiteHeader';
import SiteBackground from '../components/SiteBackground';
import api from '../utils/api';

const GroupOfficesPage = () => {
  const { groupName } = useParams();
  const navigate = useNavigate();
  const decodedGroup = decodeURIComponent(groupName || '');
  const [offices, setOffices] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchOffices = async () => {
      setLoading(true);
      try {
        const res = await api.get('/offices/active');
        const list = (res.data || []).filter(
          (o) => (o.group && o.group.trim()) === decodedGroup
        );
        setOffices(list);
      } catch (err) {
        console.error('事業所の取得に失敗しました:', err);
      } finally {
        setLoading(false);
      }
    };
    const fetchDescription = async () => {
      try {
        const res = await api.get('/offices/group-descriptions');
        const val = (res.data.descriptions || {})[decodedGroup];
        setSections(Array.isArray(val) ? val : []);
      } catch (err) {
        setSections([]);
      }
    };
    fetchOffices();
    fetchDescription();
  }, [decodedGroup]);

  return (
    <div className="min-h-screen text-gray-900 relative">
      <SiteBackground />
      <SiteHeader />

      <div className="fixed top-3 right-4 sm:top-5 sm:right-6 z-[60]">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(-1)}
          className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
          data-testid="group-back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          戻る
        </Button>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-20 relative">
        <div className="text-center mb-12 mt-[5vh]">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900" data-testid="group-title">
            {decodedGroup}
          </h1>
          {sections.length > 0 && (
            <div className="max-w-2xl mx-auto mt-6 space-y-6 text-left" data-testid="group-description">
              {sections.map((sec, idx) => (
                <div key={idx} data-testid={`group-section-${idx}`}>
                  {sec.title && (
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{sec.title}</h2>
                  )}
                  {sec.body && (
                    <div
                      className="rich-content text-gray-600 text-base sm:text-lg"
                      dangerouslySetInnerHTML={{ __html: sec.body }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center text-gray-600 py-20" data-testid="group-loading">読み込み中...</div>
        ) : offices.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600 mb-6" data-testid="group-empty">このグループの事業所はありません。</p>
            <Link to="/" className="inline-flex items-center text-amber-500 hover:text-amber-600">
              <ChevronLeft className="w-4 h-4 mr-1" />
              トップに戻る
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="group-offices-grid">
            {offices.map((office) => (
              <Link
                to={`/offices/${office.id}`}
                key={office.id}
                data-testid={`group-office-card-${office.id}`}
                className="group relative rounded-2xl bg-gradient-to-br from-gray-50/80 to-gray-100/80 border border-amber-500/20 backdrop-blur-sm overflow-hidden hover:border-amber-500/50 transition-all duration-500"
              >
                {office.image_url && (
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={office.image_url}
                      alt={office.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-50 to-transparent"></div>
                  </div>
                )}
                <div className="relative p-6">
                  <h3 className="text-2xl font-bold text-gray-900 group-hover:text-amber-500 transition-colors mb-3">
                    {office.name}
                  </h3>
                  {office.description && (
                    <p className="text-gray-600 mb-4 text-sm line-clamp-3">{office.description}</p>
                  )}
                  <div className="space-y-2 text-sm">
                    {office.address && (
                      <div className="flex items-start space-x-2 text-gray-600">
                        <MapPin className="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" />
                        <span>{office.address}</span>
                      </div>
                    )}
                    {office.phone && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Phone className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <span>{office.phone}</span>
                      </div>
                    )}
                    {office.email && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Mail className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <span>{office.email}</span>
                      </div>
                    )}
                    {office.business_hours && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <span>{office.business_hours}</span>
                      </div>
                    )}
                  </div>
                  <span className="inline-flex items-center mt-4 text-amber-500 text-sm">
                    詳細を見る
                    <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default GroupOfficesPage;
